import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { asyncScheduler, BehaviorSubject, EMPTY, from, fromEvent, merge, Observable, of, Subject, timer } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, take, takeUntil, tap, throttleTime } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
	SessionTimeoutDialogComponent,
	SessionTimeoutDialogResult,
	SESSION_TIMEOUT_MESSAGE_ID
} from '../components/session-timeout-dialog/session-timeout-dialog.component';
import { ApplicationInsightsService } from './application-insights.service';
import { MsalService } from './msal';
import { SnackbarService } from './snackbar.service';

// CAL 2 requires re-authentication after a period of inactivity; durations are configured per environment.
export const INACTIVITY_TIMEOUT_MS = environment.session.inactivityMinutes * 60 * 1000;
export const WARNING_TIMEOUT_MS = environment.session.warningMinutes * 60 * 1000;

export const LAST_ACTIVITY_STORAGE_KEY = 'osims:lastActivityAt';
export const SESSION_EXTENDED_STORAGE_KEY = 'osims:sessionExtendedAt';
export const SESSION_ENDED_STORAGE_KEY = 'osims:sessionEndedAt';

export const SESSION_EXPIRED_ROUTE = 'session-expired';

const ACTIVITY_THROTTLE_MS = 1000;
const ACTIVITY_EVENTS: ReadonlyArray<keyof WindowEventMap> = [
	'mousemove',
	'mousedown',
	'keydown',
	'touchstart',
	'scroll',
	'click'
];

@Injectable({
	providedIn: 'root'
})
export class IdleTimeoutService implements OnDestroy {
	private readonly stop$ = new Subject<void>();
	private readonly restart$ = new Subject<void>();
	private readonly warningSubject = new BehaviorSubject<boolean>(false);
	private readonly remainingSecondsSubject = new BehaviorSubject<number>(0);
	private readonly expiredSubject = new Subject<void>();

	private watching = false;
	private warningActive = false;
	private dialogRef: MatDialogRef<SessionTimeoutDialogComponent, SessionTimeoutDialogResult>;

	readonly warning$ = this.warningSubject.asObservable();
	readonly remainingSeconds$ = this.remainingSecondsSubject.asObservable();
	readonly expired$ = this.expiredSubject.asObservable();

	constructor(
		private readonly zone: NgZone,
		private readonly authService: MsalService,
		private readonly applicationInsightsService: ApplicationInsightsService,
		private readonly dialog: MatDialog,
		private readonly router: Router,
		private readonly snackbarService: SnackbarService,
		private readonly translateService: TranslateService,
	) {}

	start(): void {
		if (this.watching) {
			return;
		}
		this.watching = true;

		// Activity listeners run outside Angular to avoid triggering change detection on every event.
		this.zone.runOutsideAngular(() => {
			// WCAG 2.2.1 requires an explicit action to extend, so activity is ignored while the warning is up.
			const localActivity$ = from(ACTIVITY_EVENTS).pipe(
				mergeMap(eventName => fromEvent<Event>(window, eventName, { passive: true })),
				throttleTime(ACTIVITY_THROTTLE_MS, asyncScheduler, { leading: true, trailing: true }),
				filter(() => !this.warningActive),
				tap(() => localStorage.setItem(LAST_ACTIVITY_STORAGE_KEY, Date.now().toString()))
			);
			const storage$ = fromEvent<StorageEvent>(window, 'storage');
			const crossTabActivity$ = storage$.pipe(
				filter(event => event.key === LAST_ACTIVITY_STORAGE_KEY && !this.warningActive)
			);

			storage$.pipe(
				filter(event => event.key === SESSION_EXTENDED_STORAGE_KEY),
				takeUntil(this.stop$)
			).subscribe(() => this.zone.run(() => this.onExtendedElsewhere()));

			storage$.pipe(
				filter(event => event.key === SESSION_ENDED_STORAGE_KEY),
				takeUntil(this.stop$)
			).subscribe(() => this.zone.run(() => this.onEndedElsewhere()));

			merge(timer(0), localActivity$, crossTabActivity$, this.restart$).pipe(
				switchMap(() => this.sessionCycle()),
				takeUntil(this.stop$)
			).subscribe(() => this.zone.run(() => this.onExpired()));
		});
	}

	stop(): void {
		if (!this.watching) {
			return;
		}
		this.stop$.next();
		this.watching = false;
	}

	/**
	 * Keeps the session alive without navigating away: the token is refreshed in a hidden
	 * iframe, so unsaved work on the page survives. A silent failure ends the session.
	 */
	extend(): Observable<void> {
		const account = this.authService.getAccount();
		if (!account) {
			this.dismissWarning();
			return of(undefined);
		}

		// Broadcast up front: a silent refresh can take seconds, and the other tabs must not keep
		// counting down (or expire) while it is in flight.
		this.onExtended();

		return this.authService.acquireTokenSilent({
			scopes: [environment.auth.apiScope],
			account,
			redirectUri: environment.auth.redirectUri
		}).pipe(
			map(() => undefined),
			catchError(error => {
				this.applicationInsightsService.logException(error);
				this.onExpired();
				return EMPTY;
			})
		);
	}

	ngOnDestroy(): void {
		this.stop$.next();
		this.stop$.complete();
	}

	private sessionCycle(): Observable<number> {
		this.remainingSecondsSubject.next(Math.round(WARNING_TIMEOUT_MS / 1000));

		return timer(INACTIVITY_TIMEOUT_MS - WARNING_TIMEOUT_MS).pipe(
			tap(() => this.zone.run(() => this.onWarning())),
			switchMap(() => this.countdown())
		);
	}

	private countdown(): Observable<number> {
		const totalSeconds = Math.round(WARNING_TIMEOUT_MS / 1000);

		return timer(0, 1000).pipe(
			take(totalSeconds + 1),
			tap(elapsed => this.zone.run(() => this.remainingSecondsSubject.next(totalSeconds - elapsed))),
			filter(elapsed => elapsed === totalSeconds)
		);
	}

	private onWarning(): void {
		if (!this.authService.getAccount()) {
			return;
		}

		this.warningActive = true;
		this.warningSubject.next(true);
		this.applicationInsightsService.logEvent('SessionWarningShown', {
			warningMinutes: WARNING_TIMEOUT_MS / 60 / 1000
		});
		this.openWarningDialog();
	}

	private onExtended(): void {
		this.applicationInsightsService.logEvent('SessionExtended', {
			timeoutMinutes: INACTIVITY_TIMEOUT_MS / 60 / 1000
		});
		localStorage.setItem(SESSION_EXTENDED_STORAGE_KEY, Date.now().toString());
		this.dismissWarning();
		this.restart$.next();
	}

	private onExtendedElsewhere(): void {
		const wasWarning = this.warningActive;
		this.dismissWarning();
		this.restart$.next();
		if (wasWarning) {
			this.snackbarService.message(this.translateService.instant('Session.ExtendedElsewhere'));
		}
	}

	private onExpired(): void {
		// Dismissed before the account check so the dialog can never be stranded at 0:00.
		this.dismissWarning();

		if (!this.authService.getAccount()) {
			return;
		}

		this.stop();
		this.applicationInsightsService.logEvent('IdleTimeout', {
			timeoutMinutes: INACTIVITY_TIMEOUT_MS / 60 / 1000
		});
		localStorage.setItem(SESSION_ENDED_STORAGE_KEY, Date.now().toString());
		this.expiredSubject.next();

		this.authService.logoutLocal().subscribe({
			next: () => this.navigateToExpiredScreen(),
			error: error => {
				this.applicationInsightsService.logException(error);
				this.navigateToExpiredScreen();
			}
		});
	}

	private onEndedElsewhere(): void {
		this.stop();
		this.dismissWarning();
		this.expiredSubject.next();
		this.navigateToExpiredScreen();
	}

	// A deliberate sign-out keeps the existing Azure AD sign-out behaviour of the header button.
	private signOutNow(): void {
		this.stop();
		this.dismissWarning();
		this.authService.logout().subscribe({
			error: error => this.applicationInsightsService.logException(error)
		});
	}

	private dismissWarning(): void {
		this.warningActive = false;
		this.warningSubject.next(false);
		this.closeWarningDialog();
	}

	private openWarningDialog(): void {
		if (this.dialogRef) {
			return;
		}

		this.dialogRef = this.dialog.open(SessionTimeoutDialogComponent, {
			disableClose: true,
			autoFocus: true,
			role: 'alertdialog',
			ariaDescribedBy: SESSION_TIMEOUT_MESSAGE_ID,
			panelClass: 'popup',
			data: { remainingSeconds$: this.remainingSeconds$ }
		});

		this.dialogRef.afterClosed().subscribe(result => {
			this.dialogRef = null;
			if (result === 'extend') {
				this.extend().subscribe();
			} else if (result === 'signOut') {
				this.signOutNow();
			}
		});
	}

	private closeWarningDialog(): void {
		if (!this.dialogRef) {
			return;
		}
		const openDialog = this.dialogRef;
		this.dialogRef = null;
		openDialog.close();
	}

	private navigateToExpiredScreen(): void {
		this.router.navigateByUrl(SESSION_EXPIRED_ROUTE);
	}
}
